import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Link,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Download,
  PersonOutline,
  SmartToy,
  VolumeUp,
} from '@mui/icons-material';
import { getApiUrl } from '../services/api';

interface TranscriptEntry {
  role?: 'user' | 'assistant' | 'bot' | 'system' | string;
  transcriptType?: 'partial' | 'final';
  transcript?: string;
  message?: string;
  timestamp?: string | number;
  time?: string | number;
  secondsFromStart?: number;
}

interface SavedAnswer {
  questionIndex: number;
  questionKey?: string | null;
  questionText: string;
  value?: any;
  matchSource?: string;
}

interface AnnotatedTranscriptEntry {
  role: string;
  message: string;
  timestamp?: string | number;
  secondsFromStart?: number | null;
  savedAnswer?: SavedAnswer | null;
}

interface NormalizedTranscriptEntry {
  role: string;
  transcriptType?: 'partial' | 'final';
  message: string;
  timestamp?: string | number;
  secondsFromStart?: number | null;
  savedAnswer?: SavedAnswer | null;
}

interface VoiceCallDetailsProps {
  voiceCallLogId: string;
  hasRecording?: boolean;
  transcript?: TranscriptEntry[];
  /**
   * PHA-990. Turns already paired with the answer each one produced, resolved
   * server-side from the call's timeline (bff-server/voice/transcript-answer-matcher.js).
   *
   * This component used to pair them itself, by testing whether an answer's
   * value appeared as a substring of the turn's text. The hospitalization
   * answer's value is "yes", so every turn containing those three letters —
   * the greeting "Yes", and even the assistant saying "answer only with yes or
   * no" — was labelled as the hospitalization question. Which turn produced
   * which answer is a fact about the call's timeline, and the words alone
   * cannot recover it, so the component no longer tries.
   */
  annotatedTranscript?: AnnotatedTranscriptEntry[];
  durationSeconds?: number;
  status?: string;
  callId?: string;
}

const VoiceCallDetails: React.FC<VoiceCallDetailsProps> = ({
  voiceCallLogId,
  hasRecording,
  transcript,
  annotatedTranscript,
  durationSeconds,
  status,
  callId,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  // Construct recording URL if available
  // Note: We use the proxy endpoint to avoid exposing S3 credentials/URLs
  const recordingUrl = hasRecording 
    ? getApiUrl(`/api/voice/voice-calls/${voiceCallLogId}/recording`)
    : undefined;

  // Format timestamp
  const formatTimestamp = (timestamp?: string | number) => {
    if (timestamp === undefined || timestamp === null) {
      return '';
    }

    try {
      const numericTimestamp = typeof timestamp === 'number'
        ? timestamp
        : Number.isFinite(Number(timestamp))
          ? Number(timestamp)
          : timestamp;

      const date = new Date(numericTimestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return String(timestamp);
    }
  };

  // Format duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle play/pause
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Handle loaded metadata
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Handle audio ended
  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const rawTranscript = (() => {
    if (!transcript) {
      return [];
    }

    if (Array.isArray(transcript)) {
      return transcript;
    }

    if (typeof transcript === 'string') {
      try {
        const parsed = JSON.parse(transcript);
        return Array.isArray(parsed) ? parsed : parsed?.transcript || [];
      } catch {
        return [];
      }
    }

    return (transcript as any)?.transcript || [];
  })() as TranscriptEntry[];

  // The server-resolved turns win whenever they are present. The local pass
  // below is only for payloads that predate annotatedTranscript, and it carries
  // no labels at all — an unlabelled turn costs a reviewer one click, a wrongly
  // labelled one tells them the patient reported a hospitalization they did not.
  const normalizedTranscripts: NormalizedTranscriptEntry[] = (
    annotatedTranscript && annotatedTranscript.length > 0
  )
    ? annotatedTranscript.map((entry: AnnotatedTranscriptEntry) => ({
      role: entry.role || 'assistant',
      message: entry.message ?? '',
      timestamp: entry.timestamp,
      secondsFromStart: entry.secondsFromStart,
      savedAnswer: entry.savedAnswer ?? null,
    }))
    : rawTranscript
      .map((entry: TranscriptEntry) => {
        const messageText = entry.transcript ?? entry.message ?? '';
        const rawRole = entry.role === 'bot' ? 'assistant' : entry.role;

        return {
          role: rawRole || 'assistant',
          transcriptType: entry.transcriptType,
          message: messageText,
          timestamp: entry.timestamp ?? entry.time,
          secondsFromStart: entry.secondsFromStart,
          savedAnswer: null,
        };
      })
      .filter((entry: NormalizedTranscriptEntry) => entry.role !== 'system')
      .filter((entry: NormalizedTranscriptEntry) => entry.transcriptType !== 'partial')
      .filter((entry: NormalizedTranscriptEntry) => entry.message.trim().length > 0);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Recording Player */}
      {recordingUrl && (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <VolumeUp color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              Call Recording
            </Typography>
            {durationSeconds && (
              <Chip
                label={formatDuration(durationSeconds)}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={togglePlayPause}
              color="primary"
              size="large"
            >
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  width: '100%',
                  height: 6,
                  backgroundColor: '#ddd',
                  borderRadius: 3,
                  position: 'relative',
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  if (audioRef.current && duration > 0) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const percentage = x / rect.width;
                    audioRef.current.currentTime = percentage * duration;
                  }
                }}
              >
                <Box
                  sx={{
                    width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                    height: '100%',
                    backgroundColor: '#1976d2',
                    borderRadius: 3,
                    transition: 'width 0.1s'
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatDuration(currentTime)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDuration(duration)}
                </Typography>
              </Box>
            </Box>

            <Link
              href={recordingUrl}
              target="_blank"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Download fontSize="small" />
              <Typography variant="caption">Download</Typography>
            </Link>
          </Box>

          <audio
            ref={audioRef}
            src={recordingUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
            preload="metadata"
            crossOrigin="use-credentials"
          />
        </Paper>
      )}

      {/* Transcript */}
      {normalizedTranscripts.length > 0 ? (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            maxHeight: 500,
            overflowY: 'auto',
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Call Transcript
          </Typography>

          {normalizedTranscripts.map((entry: NormalizedTranscriptEntry, index: number) => {
            const matchedAnswer = entry.savedAnswer;
            const isAssistant = entry.role !== 'user';

            return (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  {/* Role Icon */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: entry.role === 'assistant' ? '#e3f2fd' : '#f3e5f5',
                      flexShrink: 0,
                    }}
                  >
                    {isAssistant ? (
                      <SmartToy sx={{ fontSize: 20, color: '#1976d2' }} />
                    ) : (
                      <PersonOutline sx={{ fontSize: 20, color: '#9c27b0' }} />
                    )}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color={isAssistant ? 'primary' : 'secondary'}
                      >
                        {isAssistant ? 'AI Assistant' : 'Patient'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(entry.timestamp)}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        backgroundColor: isAssistant ? '#f5f5f5' : '#fff',
                        p: 1.5,
                        borderRadius: 2,
                        border: matchedAnswer ? '2px solid #4caf50' : 'none'
                      }}
                    >
                      {entry.message}
                    </Typography>

                    {/* Show matched answer */}
                    {matchedAnswer && (
                      <Box sx={{ mt: 1, ml: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: '#4caf50',
                            fontWeight: 'bold'
                          }}
                        >
                          → Saved as: {matchedAnswer.questionText}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {index < normalizedTranscripts.length - 1 && (
                  <Divider sx={{ mt: 2 }} />
                )}
              </Box>
            );
          })}
        </Paper>
      ) : (
        <Alert severity="info">
          No transcript available for this call.
        </Alert>
      )}

      {/* Call Metadata */}
      {callId && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`Call ID: ${callId.substring(0, 8)}...`}
            size="small"
            variant="outlined"
          />
          {status && (
            <Chip
              label={status}
              size="small"
              color={status === 'completed' ? 'success' : 'default'}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default VoiceCallDetails;
